/**
 * Schedules Service
 * Handles all schedule-related API calls for events and appointments
 */

import network from '../../Network/NetworkManager';

const APPURL = require('../../Constants/URLConstants');

/**
 * Create or update schedules for a listing (bulk operation)
 * @param {number} listingId - Listing ID
 * @param {array} schedulesData - Array of schedule objects
 * @returns {Promise<object>} - Response with created/updated schedules
 *
 * Note: This is a bulk update operation. All schedules for a listing are replaced.
 * Include the 'id' field for existing schedules to update them, omit 'id' to create new ones.
 */
export const updateSchedules = async (listingId, schedulesData) => {
  try {
    const response = await network.networkCall(
      `v1/listings/${listingId}/schedules`,
      'PUT',
      JSON.stringify({ schedules: schedulesData }),
      '',
      '',
    );

    if (response.status && response.data?.schedules) {
      return response.data.schedules;
    }

    return response;
  } catch (error) {
    console.error('Error updating schedules:', error);
    throw new Error(
      error.response?.data?.message ||
      error.message ||
      'Failed to update schedules'
    );
  }
};

/**
 * Get schedules for a listing
 * @param {number} listingId - Listing ID
 * @returns {Promise<object>} - Response with schedules array
 */
export const getSchedules = async (listingId) => {
  try {
    const response = await network.networkCall(
      `v1/listings/${listingId}/schedules`,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching schedules:', error);
    throw error;
  }
};

/**
 * Get schedules per day for a listing
 * @param {number} listingId - Listing ID
 * @param {object} params - Query parameters
 * @returns {Promise<object>} - Response with schedules
 */
export const getSchedulesPerDay = async (listingId, params = {}) => {
  try {
    const { days = 9, startAt } = params;

    const queryParams = {
      days: days.toString(),
    };

    if (startAt) {
      queryParams.start_at = startAt;
    }

    const queryString = new URLSearchParams(queryParams).toString();

    const response = await network.networkCall(
      `v1/listings/${listingId}/schedules_per_day?${queryString}`,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching schedules per day:', error);
    throw error;
  }
};

/**
 * Delete a schedule
 * @param {number} listingId - Listing ID
 * @param {number} scheduleId - Schedule ID
 * @returns {Promise<object>} - Response
 */
export const deleteSchedule = async (listingId, scheduleId) => {
  try {
    const response = await network.networkCall(
      `v1/listings/${listingId}/schedules/${scheduleId}`,
      'DELETE',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error deleting schedule:', error);
    throw error;
  }
};

/**
 * Update a single schedule
 * @param {number} listingId - Listing ID
 * @param {number} scheduleId - Schedule ID
 * @param {object} scheduleData - Updated schedule data
 * @returns {Promise<object>} - Response with updated schedule
 */
export const updateSchedule = async (listingId, scheduleId, scheduleData) => {
  try {
    const response = await network.networkCall(
      `v1/listings/${listingId}/schedules/${scheduleId}`,
      'PUT',
      JSON.stringify({ schedule: scheduleData }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error updating schedule:', error);
    throw error;
  }
};
